import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { KeyRound, X, Copy, Check } from "lucide-react";
import { useAuth, API } from "../App";
import { Button } from "./ui/button";

/**
 * Sticky reminder shown to signed-in users who do NOT yet have a Secret
 * Account ID Code on file. If the user dismisses it, we snooze for 7 days
 * and re-nudge automatically. Snooze state lives in localStorage —
 * intentionally not on the server, because we WANT the reminder to come
 * back on every device the user signs in on.
 */
const SNOOZE_KEY = "recovery_reminder_snoozed_until_v1";
const SNOOZE_DAYS = 7;

const RecoveryReminderBanner = () => {
  const { token, isAuthenticated } = useAuth();
  const [visible, setVisible] = useState(false);
  const [checking, setChecking] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [savedAck, setSavedAck] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      setVisible(false);
      setChecking(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/recovery/has-code`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const hasCode = !!res.data?.has_code;
        if (hasCode) {
          // If they now have a code, clear any stale snooze
          try { localStorage.removeItem(SNOOZE_KEY); } catch { /* noop */ }
          setVisible(false);
        } else {
          const snoozed = localStorage.getItem(SNOOZE_KEY);
          const snoozedUntil = snoozed ? Number(snoozed) : 0;
          const now = Date.now();
          setVisible(!snoozedUntil || now >= snoozedUntil);
        }
      } catch {
        setVisible(false);
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, isAuthenticated]);

  const snooze = () => {
    const until = Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000;
    try { localStorage.setItem(SNOOZE_KEY, String(until)); } catch { /* noop */ }
    setVisible(false);
    toast(`Reminder snoozed. We'll re-nudge you in ${SNOOZE_DAYS} days.`);
  };

  const generateNow = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/auth/recovery/generate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const code = res.data?.recovery_code;
      if (code) {
        setNewCode(code);
        try { localStorage.removeItem(SNOOZE_KEY); } catch { /* noop */ }
      }
    } catch {
      toast.error("Could not create your recovery code — please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(newCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Couldn't copy — please select and copy manually.");
    }
  };

  const downloadCode = () => {
    const blob = new Blob(
      [`StardomFans Secret Account ID Code\n\nCode: ${newCode}\nIssued: ${new Date().toISOString()}\n\nKeep this code safe. It is the only way to recover your account if you lose access. Do not share it.`],
      { type: "text/plain" }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "stardomfans-recovery-code.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const closeCodeModal = () => {
    if (!savedAck) {
      toast.error("Please confirm you saved your code before closing.");
      return;
    }
    setNewCode("");
    setSavedAck(false);
    setVisible(false);
  };

  if (checking || !visible) return null;

  // If we just generated a code, take over the whole screen with the reveal modal
  if (newCode) {
    return (
      <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="new-recovery-code-modal">
        <div className="bg-[#0A0E18] border border-[#EC008C]/40 rounded-2xl p-6 max-w-lg w-full">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-[#EC008C]/15 border border-[#EC008C]/40 flex items-center justify-center">
              <KeyRound className="w-5 h-5 text-[#EC008C]" />
            </div>
            <h2 className="font-['Unbounded'] text-xl font-bold text-white">
              Your Secret Account ID Code
            </h2>
          </div>
          <p className="text-sm text-[#8A92A0] mb-4">
            This is the <strong className="text-white">only way</strong> to recover your account if you forget your password.
            Save it now — we will not show it again.
          </p>
          <div className="bg-[#050810] border border-[#1A2030] rounded-lg p-4 mb-4">
            <p
              className="font-mono text-2xl sm:text-3xl font-bold tracking-widest text-[#EC008C] break-all select-all"
              data-testid="new-recovery-code-value"
            >
              {newCode}
            </p>
          </div>
          <div className="flex gap-2 mb-4">
            <Button
              onClick={copyCode}
              variant="outline"
              className="flex-1 border-[#1A2030] text-white hover:bg-[#1A2030]"
              data-testid="copy-new-recovery-code-btn"
            >
              {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
              {copied ? "Copied" : "Copy"}
            </Button>
            <Button
              onClick={downloadCode}
              variant="outline"
              className="flex-1 border-[#1A2030] text-white hover:bg-[#1A2030]"
              data-testid="download-new-recovery-code-btn"
            >
              Download .txt
            </Button>
          </div>
          <label className="flex items-start gap-2 mb-4 cursor-pointer">
            <input
              type="checkbox"
              checked={savedAck}
              onChange={(e) => setSavedAck(e.target.checked)}
              className="mt-1"
              data-testid="new-recovery-code-ack"
            />
            <span className="text-[#8A92A0] text-sm">
              I have saved my Secret Account ID Code somewhere safe. I understand it will not be shown again.
            </span>
          </label>
          <Button
            onClick={closeCodeModal}
            disabled={!savedAck}
            className="w-full bg-[#EC008C] text-black hover:bg-[#FF3399] disabled:opacity-50 font-semibold"
            data-testid="close-new-recovery-code-btn"
          >
            Done
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="sticky top-0 z-40 bg-gradient-to-r from-[#EC008C]/15 via-[#EC008C]/10 to-transparent border-b border-[#EC008C]/30 px-4 py-3"
      data-testid="recovery-reminder-banner"
    >
      <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-[#EC008C]/20 border border-[#EC008C]/40 flex items-center justify-center flex-shrink-0">
          <KeyRound className="w-4 h-4 text-[#EC008C]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white font-semibold">
            You don&apos;t have a Secret Account ID Code yet
          </p>
          <p className="text-xs text-[#8A92A0]">
            Without it you can&apos;t recover access if you forget your password. Takes 10 seconds.
          </p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Button
            onClick={generateNow}
            disabled={generating}
            className="bg-[#EC008C] text-black hover:bg-[#FF3399] font-semibold h-9 text-xs px-4"
            data-testid="recovery-reminder-generate-btn"
          >
            {generating ? "Creating…" : "Generate now"}
          </Button>
          <button
            onClick={snooze}
            className="text-xs text-[#8A92A0] hover:text-white h-9 px-2"
            data-testid="recovery-reminder-snooze-btn"
            title="Remind me in 7 days"
          >
            Remind me in 7 days
          </button>
          <button
            onClick={snooze}
            className="text-[#8A92A0] hover:text-white flex-shrink-0"
            data-testid="recovery-reminder-close-btn"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecoveryReminderBanner;
